/* eslint-disable max-len */
import React from 'react';
import fastIsEqual from 'react-fast-compare';
import { Obj } from '../../../CodesForFoundation';
import { Component } from '../../../concepts/objects';
import { escapeUnsafe } from '../../../platform/Html';
import { Physical } from '../entities/Physical';
import { PreferredTextColor, textAlignmentToFlexAlign, textAlignmentToTextAlign } from '../entities/TextConventions';
import { TextUIField } from '../entities/UIField';
import { FieldLayout } from '../fields/AdaptableField';
import { TextField, TextFieldProps } from '../fields/TextField';
import { Colors } from './../../../ui';
import { RenderError } from './RenderHelpers';
import { TextRuler } from './TextRuler';

const UrlRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g;
const toAnchor = url => `<a style="text-decoration: underline;" href="${url.replace(/\.$/, '')}">${url}</a>`;

const formatMargin = (value: number | number[]) =>
    Array.isArray(value) ? value.map(x => x + 'px').join(' ') : value + 'px';

type ExtractedTextProps = TextFieldProps & { layout: TextField['layout'] };

// Yuck, sry
const suggestFontColor = (owner: Component) => {
    return owner?.as(PreferredTextColor)?.getPreferredTextColor() ?? Colors.text;
};

const extractTextProps = (field: TextField, overrideValue?: any): ExtractedTextProps =>
    Object.assign(
        Obj.pick(
            field,
            'layout', 'fontFamily',
            'maxFontSize', 'maxLength', 'fontSize',
            'fontStyle', 'fontWeight', 'lineHeight',
            'textMargin', 'textAlignH', 'textAlignV'
        ),
        {
            fontColor: field.fontColor || suggestFontColor(field.owner),
            // Note we use toString to get formatted value, is this hacky? Probably.
            value: !!overrideValue ? overrideValue : field.toString()
        }
    );

const extractTextProps2 = (field: TextUIField, overrideValue?: any): ExtractedTextProps => ({
    ...field.settings,
    maxLength: field.maxLength,
    layout: new FieldLayout(field.bounds, 2),
    fontColor: field.settings.fontColor || suggestFontColor(field.owner),
    // Note we use toString to get formatted value, is this hacky? Probably.
    value: !!overrideValue ? overrideValue : field.value,
});

const resolveFontSize = (field: ExtractedTextProps) => {
    if (!!field.fontSize) return field.fontSize;
    return Math.min(field.maxFontSize ?? 1024, TextRuler.fit(
        field.value,
        field,
        field.layout.width,
        field.layout.height,// hasWidgets ? 92 : 95,
    ));
};

type RenderTextSimpleProps = React.PropsWithChildren<ExtractedTextProps & { tid?: any, customize?: (props: any) => any }>;

const RenderTextSimpleUnoptimized = (props: RenderTextSimpleProps) => {
    const { tid, customize, ...text } = props;
    const fontSize = resolveFontSize(text);
    const initial: any = {
        display: 'flex',
        position: 'absolute',
        left: text.layout.left + 'px',
        top: text.layout.top + 'px',
        width: text.layout.width + 'px',
        height: text.layout.height + 'px',
        color: text.fontColor,
        fontFamily: text.fontFamily,
        fontSize: fontSize + 'px',
        fontWeight: text.fontWeight,
        lineHeight: (fontSize * text.lineHeight) + 'px',
        padding: formatMargin(text.textMargin),
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        boxSizing: 'border-box',
        overflow: 'hidden',
        textAlign: textAlignmentToTextAlign(text.textAlignH),
        justifyContent: textAlignmentToFlexAlign(text.textAlignH),
        alignItems: textAlignmentToFlexAlign(text.textAlignV),
    };
    const final = !!customize ? { ...initial, ...customize(initial) } : initial;
    const escaped = escapeUnsafe(text.value ?? '');
    const parsed = escaped.replace(UrlRegex, toAnchor);
    const padded = parsed.match(/\n$/) ? parsed + ' ' : parsed;
    // eslint-disable-next-line no-underscore-dangle
    const __html = padded;
    return <div data-testid={tid} style={final}>
        <span dangerouslySetInnerHTML={{ __html }}></span>
        {props.children}
    </div>;
};

const RenderTextSimple = React.memo(RenderTextSimpleUnoptimized, fastIsEqual);

export type RenderFieldProps = React.PropsWithChildren<{ object: Physical, name: string, overrideValue?: any, customize?: (props: any) => any }>;

export const RenderTextField = ({ children, object, name, overrideValue, customize }: RenderFieldProps) => {
    const field = object.field(name);
    if (field instanceof TextField) {
        return <RenderTextSimple tid={field.name} {...extractTextProps(field, overrideValue)} customize={customize} >
            {children}
        </RenderTextSimple>;
    }
    return <RenderError />;
};

export const RenderTextUIField = ({ children, object, name, overrideValue, customize }: RenderFieldProps) => {
    const field = object.uiFields.find(x => x.name === name) as TextUIField;
    if (!field) return null;
    return <RenderTextSimple {...extractTextProps2(field, overrideValue)} customize={customize} >
        {children}
    </RenderTextSimple>;
};
